/**
 * Partner Portal Store - Unified store for partner portal operations
 * Manages partner request forms, login state, and partner-specific functionality
 * Follows the exact pattern from customerPortalStore.ts
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { shallow } from 'zustand/shallow'

// TypeScript interfaces
interface PartnerRequestData {
  name: string
  email: string
  phone: string
  businessName: string
  businessAddress: string
  acceptsTerms: boolean
}

interface PartnerPortalState {
  // Form UI State
  isRequestFormOpen: boolean
  isLoginDialogOpen: boolean
  
  // Request Form State
  requestFormStatus: 'idle' | 'submitting' | 'success' | 'error'
  requestFormError: string | null
  partnerRequestData: PartnerRequestData | null
}

interface PartnerPortalActions {
  // UI Actions
  setRequestFormOpen: (open: boolean) => void
  setLoginDialogOpen: (open: boolean) => void
  
  // Form Actions
  submitPartnerRequest: (data: PartnerRequestData) => Promise<void>
  resetRequestForm: () => void
  setRequestFormStatus: (status: 'idle' | 'submitting' | 'success' | 'error') => void
  setRequestFormError: (error: string | null) => void
  
  // Utility Actions
  clearStore: () => void
}

type PartnerPortalStore = PartnerPortalState & PartnerPortalActions

const initialState: PartnerPortalState = {
  // Form UI State
  isRequestFormOpen: false,
  isLoginDialogOpen: false,
  
  // Request Form State
  requestFormStatus: 'idle',
  requestFormError: null,
  partnerRequestData: null,
}

export const usePartnerPortalStore = create<PartnerPortalStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // UI Actions
      setRequestFormOpen: (open) => {
        set({ isRequestFormOpen: open })
      },

      setLoginDialogOpen: (open) => {
        set({ isLoginDialogOpen: open })
      },

      // Form Actions
      submitPartnerRequest: async (data) => {
        set({ requestFormStatus: 'submitting', requestFormError: null })
        try {
          // Import siloApi to match Contact Us form exactly
          const { siloApi } = await import('@/lib/api/silo-api')

          // Use EXACT SAME method and syntax as Contact Us form
          const result = await siloApi.submitPublicContactForm({
            name: data.name,
            email: data.email,
            company: data.businessName,
            phone: data.phone,
            request: `Partner Portal Access Application`,
            metadata: {
              source: 'partner_portal_form',
              inquiry_type: 'partner_portal_request',
              businessName: data.businessName,
              businessAddress: data.businessAddress,
              acceptedTerms: data.acceptsTerms
            }
          })
          
          set({ 
            requestFormStatus: 'success', 
            partnerRequestData: data,
            requestFormError: null 
          })
        } catch (error: any) {
          set({ 
            requestFormStatus: 'error', 
            requestFormError: error.message || 'Failed to submit partner request. Please try again.'
          })
          throw error
        }
      },

      resetRequestForm: () => {
        set({
          requestFormStatus: 'idle',
          requestFormError: null,
          partnerRequestData: null,
          isRequestFormOpen: false
        })
      },

      setRequestFormStatus: (status) => {
        set({ requestFormStatus: status })
      },

      setRequestFormError: (error) => {
        set({ requestFormError: error })
      },

      // Utility Actions
      clearStore: () => {
        set(initialState)
      }
    }),
    {
      name: 'partner-portal-store'
    }
  )
)

// Selector hooks for specific functionality
export const usePartnerRequestForm = () => {
  const isRequestFormOpen = usePartnerPortalStore(state => state.isRequestFormOpen)
  const requestFormStatus = usePartnerPortalStore(state => state.requestFormStatus)
  const requestFormError = usePartnerPortalStore(state => state.requestFormError)
  const partnerRequestData = usePartnerPortalStore(state => state.partnerRequestData)
  const setRequestFormOpen = usePartnerPortalStore(state => state.setRequestFormOpen)
  const submitPartnerRequest = usePartnerPortalStore(state => state.submitPartnerRequest)
  const resetRequestForm = usePartnerPortalStore(state => state.resetRequestForm)
  
  return {
    isRequestFormOpen,
    requestFormStatus,
    requestFormError,
    partnerRequestData,
    setRequestFormOpen,
    submitPartnerRequest,
    resetRequestForm,
  }
}

export const usePartnerLogin = () => {
  const isLoginDialogOpen = usePartnerPortalStore(state => state.isLoginDialogOpen)
  const setLoginDialogOpen = usePartnerPortalStore(state => state.setLoginDialogOpen)
  
  return {
    isLoginDialogOpen,
    setLoginDialogOpen,
  }
}